import {
  ACCOUNT_STATUS_CREATED,
  ACCOUNT_STATUS_DISABLED,
  ACCOUNT_STATUS_PARTIALLY_CREATED,
  ACCOUNT_STATUS_PENDING,
} from 'src/pages/accountSettings/components/AccountDetails/constants';
import {
  AccountStatus,
  BillToType,
  Business,
  Portal,
  SellingAccount,
  SellingAccountType,
} from '@models/account';
import { MessageBundle } from '@amzn/arb-tools';
import {
  SELLING_ACCOUNTS_AUTHOR_PORTAL,
  SELLING_ACCOUNTS_AUTHOR_TYPE,
  SELLING_ACCOUNTS_GROCERY_PORTAL,
  SELLING_ACCOUNTS_KDP_PORTAL,
  SELLING_ACCOUNTS_MERCHANT_PORTAL,
  SELLING_ACCOUNTS_SELLER_PORTAL,
  SELLING_ACCOUNTS_SELLER_TYPE,
  SELLING_ACCOUNTS_VENDOR_PORTAL,
  SELLING_ACCOUNTS_VENDOR_TYPE,
} from '@features/account-settings/constants';
import castArray from 'lodash/castArray';
import keyBy from 'lodash/keyBy';
import get from 'lodash/get';
import isNil from 'lodash/isNil';
import find from 'lodash/find';
import { LocationOption } from '@services/address';
import { FieldValues } from 'react-hook-form';
import { CreateAccountPayload } from '@services/account/types';
import { useDispatch } from 'react-redux';
import { accountApi } from '@services/account/account';

export const accountStatusTranslationKeys: Record<AccountStatus, string> = {
  [AccountStatus.Created]: ACCOUNT_STATUS_CREATED,
  [AccountStatus.Pending]: ACCOUNT_STATUS_PENDING,
  [AccountStatus.Disabled]: ACCOUNT_STATUS_DISABLED,
  [AccountStatus.PartiallyCreated]: ACCOUNT_STATUS_PARTIALLY_CREATED,
};

export const sellingAccountTypeTranslationKeys: Record<
  SellingAccountType,
  string
> = {
  AMAZON_VENDOR: SELLING_ACCOUNTS_VENDOR_TYPE,
  AMAZON_SELLER: SELLING_ACCOUNTS_SELLER_TYPE,
  AMAZON_AUTHOR: SELLING_ACCOUNTS_AUTHOR_TYPE,
};

export const sellingAccountPortalTranslationKeys: Record<Portal, string> = {
  VENDOR_CENTRAL: SELLING_ACCOUNTS_VENDOR_PORTAL,
  SELLER_CENTRAL: SELLING_ACCOUNTS_SELLER_PORTAL,
  AUTHOR_CENTRAL: SELLING_ACCOUNTS_AUTHOR_PORTAL,
  GROCERY_CENTRAL: SELLING_ACCOUNTS_GROCERY_PORTAL,
  KDP_CENTRAL: SELLING_ACCOUNTS_KDP_PORTAL,
  MERCH: SELLING_ACCOUNTS_MERCHANT_PORTAL,
};

export const SellingProgram: Record<SellingAccountType, string> = {
  AMAZON_SELLER: 'SELLER',
  AMAZON_VENDOR: 'VENDOR',
  AMAZON_AUTHOR: 'AUTHOR',
};

export const getTranslationKey = (
  value: string,
  translationKeys: Record<string, string>,
): string | undefined => {
  return translationKeys[value];
};

export const getSellingAccountType = (
  intl: MessageBundle,
  type: SellingAccountType,
): string => {
  const key = getTranslationKey(type, sellingAccountTypeTranslationKeys);
  if (!key) {
    console.error(`unable to translate selling account type ${type}`);
    return '';
  }
  return intl.getMessage(key);
};

/**
 * Translates each Portal in `portals`. If there is more than one portal, it joins
 * the translated strings together with a `/` (ie, "Vendor Central/Grocery Central").
 *
 * @param intl Panther message bundle for translations
 * @param portals List of Portal strings
 * @returns Joined portals string
 */
export const getSellingAccountPortalsString = (
  intl: MessageBundle,
  portals: Portal[],
): string => {
  if (portals == null || portals.length == 0) {
    return '';
  }
  return portals
    .map((portal) => {
      const key = getTranslationKey(
        portal,
        sellingAccountPortalTranslationKeys,
      );
      if (!key) {
        console.error(`unable to translate selling account portal ${portal}`);
        return '';
      }
      return intl.getMessage(key);
    })
    .filter((portal) => portal.length > 0) // omit failed translations
    .join('/');
};

export const getAccountStatus = (
  intl: MessageBundle,
  status: AccountStatus,
): string => {
  const key = getTranslationKey(status, accountStatusTranslationKeys);
  if (!key) {
    console.error(`unable to translate account status ${status}`);
    return intl.getMessage(
      getTranslationKey(AccountStatus.Pending, accountStatusTranslationKeys)!,
    );
  }
  return intl.getMessage(key);
};

const joinWithPipe = (arr: string[]): string => {
  return arr.filter((item) => item !== '' && item !== undefined).join(' | ');
};

export const getCountryCodeTranslation = (
  intl: MessageBundle,
  countryCode: string,
): string => {
  try {
    return intl.getMessage(countryCode);
  } catch (e) {
    console.error(`unable to translate countryCode ${countryCode}`, e);
    return '';
  }
};

export const formatCountryCodesWithPipe = (
  countryList: LocationOption[],
  countryCodes: string[] | string = [],
): string => {
  const countryCodeToDetailsMap = keyBy(countryList, 'value');
  return joinWithPipe(
    castArray(countryCodes).map((countryCode) =>
      get(countryCodeToDetailsMap, [countryCode, 'label'], countryCode),
    ),
  );
};

export const getAccountCreationRequestFromFormData = (
  data: FieldValues,
): CreateAccountPayload => {
  const {
    displayName,
    selectedSellingAccountId,
    sellingAccounts,
    business,
    industry,
    currency,
    timeZone,
    managerAccountId,
  } = data;
  const {
    businessName, // The individual address fields are passed when the user manually fills in an address
    zipCode,
    phone,
    website,
    city,
    countryCode,
    addressLine1,
    addressLine2,
    state,
    businessRegistrationNumber,
  } = business;

  let sellingAccount: SellingAccount | undefined = undefined;
  if (!isNil(selectedSellingAccountId)) {
    sellingAccount = find(sellingAccounts, {
      sellingAccountLinkToken: selectedSellingAccountId,
    });
  }

  /**
   * Previously, we would pass the AddressToken directly into the BFF API and allow GRS
   * to expand it to individual fields. However this broke for M2, as some addresses for
   * sellers and vendors do not contain all of the necessary fields for SGA registration.
   * Instead, we now pre-populate the form fields with whatever values are in the address
   * and send it to the account creation request as normal form data.
   */
  const businessDetails: Business = {
    businessName,
    zipCode,
    phone,
    website,
    city,
    countryCode,
    addressLine1,
    addressLine2,
    state,
    businessRegistrationNumber,
  };

  return {
    displayName,
    industry,
    currency,
    timeZone,
    managerAccountId,
    ...(isNil(sellingAccount)
      ? {}
      : {
          sellingAccount: {
            sellingAccountId: selectedSellingAccountId,
            sellingAccountType: sellingAccount?.sellingProgram,
            countryCode: castArray(sellingAccount?.countryCodes)[0],
          },
        }),
    business: businessDetails,
    billToType: BillToType.AdvertiserAccount,
  };
};

export const getSellingAccount = async (
  sellingAccountLinkToken: string,
  sellingProgram: SellingAccountType,
) => {
  const dispatch = useDispatch();

  const sellingAccountPromise = dispatch<any>(
    accountApi.endpoints.querySellingAccount.initiate({
      sellingAccountLinkToken,
      sellingProgram,
    }),
  );

  const { data } = await sellingAccountPromise;
  return get(data, ['sellingAccounts', 0]) as SellingAccount;
};
